import { MenuSearch } from '@fs/zion-icon'
import { useHistory } from '@fs/zion-router'
import { Autosuggest, IconButton, Row, Select } from '@fs/zion-ui'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../hooks/useNotifications'
import { addToLookupHistory, getLookupHistory } from '../../services/lookupHistoryService'
import { getReservationPersonIds } from '../../services/reservationsService'
import { getEntity, mrnRemoveDashes } from '../../util/EntityUtil'
import { getLastPathElement } from '../../util/StringUtil'
import { getFrontendBaseUrl } from '../../util/UrlUtil'
import {
  ARTIFACT_CONTEXT,
  ASSOCIATION_RELATIONSHIP_ID_CONTEXT,
  CHAT_CONTEXT,
  CIS_USER_ID_CONTEXT,
  CMIS_ID_CONTEXT,
  CONTACT_NAME_CONTEXT,
  COUPLE_RELATIONSHIP_ID_CONTEXT,
  CTR_ID_CONTEXT,
  DISCUSSION_ID_CONTEXT,
  GROUP_ID_CONTEXT,
  LookupContext,
  MRN_ID_CONTEXT,
  MEMBERSHIP_UUID_CONTEXT,
  OWS_OSID_CONTEXT,
  OWS_ID_CONTEXT,
  PARENT_CHILD_RELATIONSHIP_ID_CONTEXT,
  PERSON_ID_CONTEXT,
  PIN_ID_CONTEXT,
  RAN_ID_CONTEXT,
  REQUEST_ID_CONTEXT,
  REQUEST_OSID_CONTEXT,
  TEMPLE_CODE_CONTEXT,
  TEMPLE_SAFE_CONTEXT,
  TREE_ID_CONTEXT,
  UNIT_NUMBER_CONTEXT,
  USERNAME_CONTEXT,
  USER_ID_CONTEXT,
  MESSAGE_ID_CONTEXT,
} from './LookupContext'
import { PermissionsContext } from './PermissionsContext'
import { getChatMessage } from '../../services/chatService'

const PREFIX_TO_CONTEXT_MAPPING = {
  'a.': ARTIFACT_CONTEXT,
  'assn.': ASSOCIATION_RELATIONSHIP_ID_CONTEXT,
  'chat.': CHAT_CONTEXT,
  'cis.': CIS_USER_ID_CONTEXT,
  'cis.user.': CIS_USER_ID_CONTEXT,
  'cmis.': CMIS_ID_CONTEXT,
  'cn.': CONTACT_NAME_CONTEXT,
  'cr.': COUPLE_RELATIONSHIP_ID_CONTEXT,
  'ctr.': CTR_ID_CONTEXT,
  'ds.': DISCUSSION_ID_CONTEXT,
  'ds.disc.': DISCUSSION_ID_CONTEXT,
  'g.': GROUP_ID_CONTEXT,
  'm.': MESSAGE_ID_CONTEXT,
  'mrn.': MRN_ID_CONTEXT,
  'osid.': OWS_OSID_CONTEXT,
  'oosid.': OWS_OSID_CONTEXT,
  'ows.': OWS_ID_CONTEXT,
  'p.': PERSON_ID_CONTEXT,
  'pcr.': PARENT_CHILD_RELATIONSHIP_ID_CONTEXT,
  'pin.': PIN_ID_CONTEXT,
  'ran.': RAN_ID_CONTEXT,
  'req.': REQUEST_ID_CONTEXT,
  'rqid.': REQUEST_ID_CONTEXT,
  'rosid.': REQUEST_OSID_CONTEXT,
  't.': TEMPLE_CODE_CONTEXT,
  'ts.': TEMPLE_SAFE_CONTEXT,
  'tree.': TREE_ID_CONTEXT,
  'u.': USER_ID_CONTEXT,
  'un.': USERNAME_CONTEXT,
  'unit.': UNIT_NUMBER_CONTEXT,
  'uuid.': MEMBERSHIP_UUID_CONTEXT,
}
function Lookup() {
  const [t] = useTranslation()

  /**
   * Variables
   */
  // contexts
  const { error } = useNotifications()
  const { lookupContext, setLookupContext, lookupValue } = useContext(LookupContext) // lookupContext is a string

  // states
  const [inputText, setInputText] = useState('')
  const [suggestions, setSuggestions] = useState([])

  // refs for focus
  const textFieldRef = useRef()
  const contextRef = useRef()
  const iconRef = useRef()
  const { isRole } = useContext(PermissionsContext)

  // router history
  const history = useHistory()

  /**
   * Event Handlers
   */
  // useEffect
  useEffect(() => {
    const lookupHistory = getLookupHistory(lookupContext)
    contextRef.current.value = lookupContext
    const newSuggestions = lookupHistory.map((item, index) => {
      return { key: `${index}`, primaryText: item.value, secondaryText: item.description }
    })
    setSuggestions(newSuggestions)
  }, [lookupContext])

  useEffect(() => {
    if (lookupValue || lookupValue === '') {
      setInputText(lookupValue)
    }
  }, [lookupValue])

  // Listeners
  const onContextChange = (newLookupContext) => {
    setLookupContext(newLookupContext)
    textFieldRef.current.select()
    contextRef.current.value = newLookupContext
    setInputText('')
    // textFieldRef.current.click()
    // // eslint-disable-next-line babel/no-unused-expressions
    // textFieldRef.current ? textFieldRef.current.focus() : null
  }

  const onTextChange = (value) => {
    if (!value) {
      setInputText('')
      return // when you click outside of autosuggest it sends an empty string
    }
    if (value.indexOf('.') === -1) {
      setInputText(value)
      return
    }
    // need to consider that username and contact names might have '.'
    let prefix = value.substring(0, value.indexOf('.') + 1)
    let id = value.substring(value.indexOf('.') + 1)
    const context = PREFIX_TO_CONTEXT_MAPPING[prefix.toLowerCase()]

    // if it cannot find a context - the "prefix" is probably part of the username or contact name
    if (context === undefined) {
      setInputText(value)
    } else if (context !== lookupContext && !id) {
      // if we are here it is because we typed a prefix into the textField and we want to clear it
      setLookupContext(context)
      contextRef.current.value = context
    }
    // the cis context can have prefixes:  cis.  and cis.user.
    if ([CIS_USER_ID_CONTEXT, DISCUSSION_ID_CONTEXT].includes(context)) {
      prefix = value.substring(0, value.lastIndexOf('.') + 1)
      id = value.substring(value.lastIndexOf('.') + 1)
    }

    if (context) {
      setLookupContext(context)
      setInputText(id)
      contextRef.current.value = context
    }
  }

  const onLookup = async (context, value) => {
    let entityValue = 0
    if (!(context && value)) {
      return
    }
    // cleaning white spaces
    value = value.trim()
    value = value.replace(/\u200B/g, '')
    // getting EntityValue
    switch (context) {
      case ASSOCIATION_RELATIONSHIP_ID_CONTEXT:
      case CHAT_CONTEXT:
      case COUPLE_RELATIONSHIP_ID_CONTEXT:
      case CIS_USER_ID_CONTEXT:
      case CTR_ID_CONTEXT:
      case DISCUSSION_ID_CONTEXT:
      case GROUP_ID_CONTEXT:
      case MESSAGE_ID_CONTEXT:
      case OWS_ID_CONTEXT:
      case PARENT_CHILD_RELATIONSHIP_ID_CONTEXT:
      case PERSON_ID_CONTEXT:
      case PIN_ID_CONTEXT:
      case RAN_ID_CONTEXT:
      case TEMPLE_CODE_CONTEXT:
      case TREE_ID_CONTEXT:
      case USER_ID_CONTEXT:
        entityValue = getEntity(value).alphaNumericId
        if (entityValue.length > 0) {
          setInputText(entityValue)
          addToLookupHistory(context, entityValue)
        } else {
          entityValue = value
        }
        break
      case MRN_ID_CONTEXT:
        value = mrnRemoveDashes(value)
        setInputText(value)
        addToLookupHistory(context, value)
        break
      default:
        setInputText(value)
        addToLookupHistory(context, value)
    }
    const currentPath = history.location.pathname
    switch (context) {
      case ARTIFACT_CONTEXT:
        history.push(`/artifacts/artifact/${value}`)
        break
      case CHAT_CONTEXT:
        history.push(`/chat/${value}/summary`)
        break
      case CIS_USER_ID_CONTEXT:
        history.push(`/user/${entityValue}/summary?context=cisUserId`)
        break
      case CMIS_ID_CONTEXT:
        history.push(`/user/${value}/summary?context=cmisId&forward=true`)
        break
      case CONTACT_NAME_CONTEXT:
        history.push(`/user/${value.replace(/"/g, '')}/summary?context=contactName`)
        break
      case CTR_ID_CONTEXT:
        history.push(`/temple/ctr/${entityValue}/summary`)
        break
      case DISCUSSION_ID_CONTEXT:
        window.open(`${getFrontendBaseUrl(true)}/tree/sources/admin?discussionId=ds.disc.${entityValue}`, '_blank')
        break
      case GROUP_ID_CONTEXT:
        history.push(`/group/${entityValue}/summary`)
        break
      case MEMBERSHIP_UUID_CONTEXT:
        history.push(`/user/${value}/summary?context=uuid&forward=true`)
        break
      case MESSAGE_ID_CONTEXT:
        try {
          const messageResponse = await getChatMessage(value)
          if (messageResponse.data.parentMessageId) {
            const parentMessageResponse = await getChatMessage(messageResponse.data.parentMessageId)
            history.push(
              `/chat/${parentMessageResponse.data.cid}/messages?parentMessageId=${parentMessageResponse.data.messageId}&messageId=${messageResponse.data.messageId}`
            )
          } else {
            history.push(`/chat/${messageResponse.data.cid}/messages?messageId=${messageResponse.data.messageId}`)
          }
        } catch (err) {
          error(
            `${t('chat.errorFetchingMessage', {
              defaultValue: 'Something went wrong fetching message',
            })}${err.message ? `: ${err.message}` : ''}`,
            err
          )
        }
        break
      case MRN_ID_CONTEXT:
        value = mrnRemoveDashes(value)
        value = value.toUpperCase()
        history.push(`/user/${value}/summary?context=mrn&forward=true`)
        break
      case OWS_OSID_CONTEXT:
        history.push(`/temple/ows/${value}/summary?idType=osid`)
        break
      case OWS_ID_CONTEXT: {
        history.push(`/temple/ows/${entityValue}/summary`)
        break
      }
      case PERSON_ID_CONTEXT:
        if (currentPath.startsWith('/person/')) {
          history.push(`/person/${entityValue}/${getLastPathElement(history.location.pathname)}?forward=true`)
        } else {
          history.push(`/person/${entityValue}/summary?forward=true`)
        }
        break
      case PIN_ID_CONTEXT:
        history.push(`/temple/inventory-info/${entityValue}/summary`)
        break
      case RAN_ID_CONTEXT:
        getReservationPersonIds(entityValue)
          .then((response) => {
            const personIds = response.data
            if (personIds && personIds.length > 0) {
              history.push(`/person/${personIds[0]}/reservations?ranId=${entityValue}`)
            }
          })
          .catch((err) => {
            error(`Error getting reservation person IDs: ${err.message || ''}`, err)
          })
        break
      case COUPLE_RELATIONSHIP_ID_CONTEXT:
        history.push(`/relationship/couple/${entityValue}/summary`)
        break
      case PARENT_CHILD_RELATIONSHIP_ID_CONTEXT:
        history.push(`/relationship/parentchild/${entityValue}/summary`)
        break
      case ASSOCIATION_RELATIONSHIP_ID_CONTEXT:
        history.push(`/relationship/association/${entityValue}/summary`)
        break
      case REQUEST_ID_CONTEXT:
        history.push(`/temple/request/${value}`)
        break
      case REQUEST_OSID_CONTEXT:
        history.push(`/temple/request/${value}?idType=osid`)
        break
      case TEMPLE_CODE_CONTEXT:
        history.push(`/temple/inventory?templeCode=${entityValue}`)
        break
      case TEMPLE_SAFE_CONTEXT:
        history.push(`/queues/${value}`)
        break
      case TREE_ID_CONTEXT:
        history.push(`/tree/${entityValue}/summary`)
        break
      case USER_ID_CONTEXT:
        history.push(`/user/${entityValue}/summary`)
        break
      case USERNAME_CONTEXT:
        history.push(`/user/${value.replace(/"/g, '')}/summary?context=username`)
        break
      case UNIT_NUMBER_CONTEXT:
        history.push(`/unit/${value}`)
        break
      default:
      // NOTIFY: Context is invalid (this shouldn't happen because contexts are selected from a static list))
    }
  }

  /**
   * Helps UI
   */
  const dropdownOptions = [
    {
      value: ARTIFACT_CONTEXT,
      label: t('artifacts.artifactId', 'Artifact ID'),
    },
    {
      value: ASSOCIATION_RELATIONSHIP_ID_CONTEXT,
      label: t('lookup.associationId', 'Association ID'),
    },
    isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6']) && {
      value: CHAT_CONTEXT,
      label: t('chat.channelId', 'Channel ID'),
    },
    {
      value: CIS_USER_ID_CONTEXT,
      label: t('cisUserId', 'CIS User ID'),
    },
    {
      value: CMIS_ID_CONTEXT,
      label: t('cmisId', 'CMIS ID'),
    },
    {
      value: CONTACT_NAME_CONTEXT,
      label: t('contactName', 'Contact Name'),
    },
    {
      value: COUPLE_RELATIONSHIP_ID_CONTEXT,
      label: t('lookup.coupleId', 'Couple ID'),
    },
    {
      value: CTR_ID_CONTEXT,
      label: t('ctrId', 'CTR ID'),
    },
    {
      value: DISCUSSION_ID_CONTEXT,
      label: t('discussionId', 'Discussion ID'),
    },
    isRole(['Data Admin 5', 'Data Admin 6', 'Engineer Admin']) && {
      value: GROUP_ID_CONTEXT,
      label: t('lookup.groupId', 'Group ID'),
    },
    isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6']) && {
      value: MESSAGE_ID_CONTEXT,
      label: t('chat.messageId', 'Message ID'),
    },
    {
      value: MRN_ID_CONTEXT,
      label: t('mrn', 'MRN'),
    },
    {
      value: OWS_ID_CONTEXT,
      label: t('ows.owsId', 'OWS ID'),
    },
    {
      value: OWS_OSID_CONTEXT,
      label: t('lookup.osid', 'OWS OSID'),
    },
    {
      value: PARENT_CHILD_RELATIONSHIP_ID_CONTEXT,
      label: t('lookup.parentChildId', 'Parent-Child ID'),
    },
    {
      value: PERSON_ID_CONTEXT,
      label: t('personId', 'Person ID'),
    },
    {
      value: PIN_ID_CONTEXT,
      label: t('pinId', 'PIN ID'),
    },
    {
      value: RAN_ID_CONTEXT,
      label: t('reservations.ranId', 'RAN ID'),
    },
    {
      value: REQUEST_ID_CONTEXT,
      label: t('temple.request.requestId', 'Request ID'),
    },
    {
      value: REQUEST_OSID_CONTEXT,
      label: t('lookup.requestOsid', 'Request OSID'),
    },
    {
      value: TEMPLE_CODE_CONTEXT,
      label: t('templeCode', 'Temple Code'),
    },
    {
      value: TEMPLE_SAFE_CONTEXT,
      label: t('lookup.templesafe', 'TempleSafe'),
    },
    {
      value: TREE_ID_CONTEXT,
      label: t('treeId', 'Tree ID'),
    },
    {
      value: UNIT_NUMBER_CONTEXT,
      label: t('unit.unitNumber', 'Unit Number'),
    },
    {
      value: USER_ID_CONTEXT,
      label: t('userId', 'User ID'),
    },
    {
      value: USERNAME_CONTEXT,
      label: t('userName', 'Username'),
    },
    {
      value: MEMBERSHIP_UUID_CONTEXT,
      label: t('lookup.uuid', 'UUID (Memb)'),
    },
  ]

  const getPlaceholder = () => {
    if (lookupContext) {
      const lookupType = lookupContext.toLowerCase().endsWith('name') ? t('lookup.type.name', 'name') : t('id', 'ID')
      return t('lookup.placeHolder.alternate', {
        defaultValue: 'Enter {{lookupType}} for lookup...',
        lookupType,
      })
    }
    return t('lookup.placeHolder.default', 'Select an ID type...')
  }

  const isDisable = () => {
    return inputText === ''
  }

  const onEnterKey = () => {
    onLookup(lookupContext, inputText)
    // Fix for double-click on any User/Person Routes. The focus must be away from the Autosuggestion
    // for the Routes (Reservations for example) to work the first time we click on it.
    iconRef.current.focus()
  }

  /**
   * UI
   */
  return (
    <Row gutters="pico">
      <Select
        id="searchSelect"
        aria-label={t('lookup.placeHolder.select', 'Select ID Context')}
        value={contextRef.current?.value}
        onChange={(event) => onContextChange(event.target.value)}
        options={dropdownOptions.filter((option) => option)}
        ref={contextRef}
      />
      <Autosuggest
        id="idSearchInput"
        onInputTextChange={(newText) => onTextChange(newText)}
        inputText={inputText}
        clearable
        onClear={() => {
          const updatedSuggestions = getLookupHistory(lookupContext).map((item, index) => {
            return { key: `${index}`, primaryText: item.value, secondaryText: item.description }
          })
          setSuggestions(updatedSuggestions)
          setInputText('')
          return null
        }}
        suggestions={suggestions.filter((suggestion) => suggestion.primaryText !== inputText)}
        onSuggestionSelected={(selection) => {
          if (selection && lookupValue !== selection.primaryText) {
            onLookup(lookupContext, selection.primaryText)
          }
        }}
        placeholder={getPlaceholder()}
        ref={textFieldRef}
        inputProps={{ onEnterKey }}
        onFocus={() => textFieldRef.current?.select()}
        handleInput
        clearOnClickAway
        keepUserInputTextAlways
      />

      <IconButton
        id="searchBtn"
        title={t('action.search', 'Search')}
        onClick={() => onLookup(lookupContext, inputText)}
        disabled={isDisable()}
        Icon={MenuSearch}
        ref={iconRef}
      />
    </Row>
  )
}

export default Lookup
